import { useEffect, useState } from "react";
import {
  Box, Typography, Paper, Grid, TextField, Button,
  Table, TableHead, TableRow, TableCell, TableBody,
  CircularProgress, Alert, Chip, Dialog, DialogTitle,
  DialogContent, IconButton, MenuItem, InputAdornment,
  Collapse, Divider,
} from "@mui/material";

import ExpandMoreIcon    from "@mui/icons-material/ExpandMore";
import ExpandLessIcon    from "@mui/icons-material/ExpandLess";
import CloseIcon         from "@mui/icons-material/Close";
import SearchIcon        from "@mui/icons-material/Search";
import FilterListIcon    from "@mui/icons-material/FilterList";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import LocationOnIcon    from "@mui/icons-material/LocationOn";
import TwoWheelerIcon    from "@mui/icons-material/TwoWheeler";
import SwapHorizIcon     from "@mui/icons-material/SwapHoriz";
import PersonIcon        from "@mui/icons-material/Person";
import PaymentIcon       from "@mui/icons-material/Payment";
import ReceiptLongIcon   from "@mui/icons-material/ReceiptLong";
import SellIcon          from "@mui/icons-material/Sell";

import { exchangeBikeService } from "../service/ExchangeBikeService";
import api from "../service/api";
import ExchangeSellModal from "../component/ExchangeSellModal";
import UpdateExchangePaymentModal from "../component/UpdateExchangePaymentModal";

// ─── THEME ───────────────────────────────────────────────────────────────────
const LOC_COLOR = {
  Mahisadal:      { bg: "#e8f4fd", border: "#1976d2", badge: "#1565c0", light: "#bbdefb" },
  Chaitanyapur: { bg: "#f3e5f5", border: "#7b1fa2", badge: "#6a1b9a", light: "#e1bee7" },
  Boroda:    { bg: "#e8f5e9", border: "#2e7d32", badge: "#1b5e20", light: "#c8e6c9" },
   Other:        { bg: "#fff8e1", border: "#f57f17", badge: "#e65100", light: "#ffecb3" }
};
const LOCATIONS = ["Chaitanyapur", "Boroda", "Mahisadal"];

// ─── SMALL HELPERS ────────────────────────────────────────────────────────────
const Label = ({ children }) => (
  <Typography variant="caption" sx={{ color: "#777", fontWeight: 600, fontSize: "0.63rem", letterSpacing: 0.5, textTransform: "uppercase" }}>
    {children}
  </Typography>
);
const Val = ({ children, color }) => (
  <Typography variant="body2" fontWeight={700} color={color || "#111"} mt={0.1}>
    {children || "—"}
  </Typography>
);
const InfoBlock = ({ label, value, color }) => (
  <Box><Label>{label}</Label><Val color={color}>{value}</Val></Box>
);

// ─── MAIN ────────────────────────────────────────────────────────────────────
const ExchangeBikeAdminView = () => {

  // ── list / filter state ───────────────────────────────────────────────────
  const [data,        setData]        = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState("");
  const [expandedLoc, setExpandedLoc] = useState({});
  const [filtersOpen, setFiltersOpen] = useState(true);

  const [filters, setFilters] = useState({
    model: "", location: "", status: "",
    registrationNumber: "", ownerName: "", mobile: "",
    fromDate: "", toDate: "", month: "", year: "", search: ""
  });

  // ── detail view modal ─────────────────────────────────────────────────────
  const [viewOpen,       setViewOpen]       = useState(false);
  const [selected,       setSelected]       = useState(null);
  const [selectedSaleId, setSelectedSaleId] = useState(null);
  const [detailLoading,  setDetailLoading]  = useState(false);
  const [soldInfo,       setSoldInfo]       = useState(null);

  // ── sell modal ────────────────────────────────────────────────────────────
  const [sellOpen, setSellOpen] = useState(false);
  const [sellBike, setSellBike] = useState(null);

  // ── update exchange payment modal ─────────────────────────────────────────
  const [openUpdate, setOpenUpdate] = useState(false);
  const [updateBike, setUpdateBike] = useState(null);

  // ── Fetch list ─────────────────────────────────────────────────────────────
  const fetchData = async () => {
    try {
      setLoading(true);
      setError("");
      const res     = await exchangeBikeService.getExchangeBikes(filters);
      const summary = res?.summary  || [];
      const vehicles = res?.vehicles || [];
      console.log("API response:", res);

      const merged = summary.map((loc) => ({
        ...loc,
        bikes: vehicles.filter((b) => b.location === loc.location)
      }));

      setData(merged);
      const exp = {};
      merged.forEach((l) => { if (l.location) exp[l.location] = true; });
      exp["Other"] = true;
      setExpandedLoc(exp);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load exchange bikes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line

  const handleChange = (e) => setFilters((p) => ({ ...p, [e.target.name]: e.target.value }));
  const resetFilters = () => {
    setFilters({ model: "", location: "", status: "", registrationNumber: "", ownerName: "", mobile: "", fromDate: "", toDate: "", month: "", year: "", search: "" });
    setTimeout(fetchData, 0);
  };
  const toggleLoc = (loc) => setExpandedLoc((p) => ({ ...p, [loc]: !p[loc] }));

  // ── View detail ────────────────────────────────────────────────────────────
  const handleView = async (id) => {
    try {
      setViewOpen(true);
      setDetailLoading(true);
      setSelected(null);
      setSelectedSaleId(null);
      setSoldInfo(null);

      const res = await api.get(`/specific-bike-details/${id}`);

      // API: res.data = { success, message, data: { ...stockDoc, ExchangeSellId: {...saleDoc} } }
      const d = res.data?.data;
      console.log("Detail response:", d);

      // Stock doc is at res.data.data
      setSelected(d);

      // Sale info lives inside the populated ExchangeSellId field
      const saleDoc = d?.ExchangeSellId;
      if (saleDoc) {
        setSelectedSaleId(saleDoc._id || null);
        setSoldInfo({
          invoiceNo:    saleDoc.invoiceNo,
          customerName: saleDoc.customerName,
          mobile:       saleDoc.mobile,
          saleDate:     saleDoc.saleDate,
          location:     saleDoc.location,
          model:        saleDoc.model,
          companyName:  saleDoc.companyName,
          engineNumber: saleDoc.engineNumber,
          chasisNumber: saleDoc.chasisNumber,
          salePrice:    saleDoc.salePrice,
          saleType:     saleDoc.saleType,
          cashAmount:   saleDoc.cashAmount,
          exchange:     saleDoc.exchange || null,
        });
      }

    } catch {
      setError("Failed to load bike details");
    } finally {
      setDetailLoading(false);
    }
  };

  // ── Sell modal ────────────────────────────────────────────────────────────
  const handleSellOpen   = (bike) => { setSellBike(bike);   setSellOpen(true);   };

  // ── Update payment modal ──────────────────────────────────────────────────
  const handleUpdateOpen = (bike) => { setUpdateBike(bike); setOpenUpdate(true); };

  // ── Summary cards ──────────────────────────────────────────────────────────
  const totalBikes = data.reduce((s, l) => s + (l.totalExchange || 0), 0);
  const totalValue = data.reduce((s, l) => s + (l.totalValue    || 0), 0);
  const totalStock = data.reduce((s, l) => s + (l.inStock       || 0), 0);
  const totalSold  = data.reduce((s, l) => s + (l.sold          || 0), 0);

  // payments from soldInfo
  const payments = soldInfo?.exchange?.payments || [];

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ maxWidth: "1200px", mx: "auto", px: 2 }}>

      {/* ── HEADER ── */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2.5} flexWrap="wrap" gap={1}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box sx={{ bgcolor: "#e65100", borderRadius: 2, p: 1, display: "flex" }}>
            <SwapHorizIcon sx={{ color: "#fff", fontSize: 26 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={800} color="#0d1b2a" letterSpacing={-0.5}>
              Exchange Bikes
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {totalBikes} total · Showroom-wise view
            </Typography>
          </Box>
        </Box>
        <Button
          startIcon={<FilterListIcon />}
          variant={filtersOpen ? "contained" : "outlined"}
          size="small"
          onClick={() => setFiltersOpen((p) => !p)}
          sx={{ borderRadius: 2, fontWeight: 700, fontSize: "0.75rem" }}
        >
          {filtersOpen ? "Hide Filters" : "Show Filters"}
        </Button>
      </Box>

      {/* ── FILTERS ── */}
      <Collapse in={filtersOpen}>
        <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, border: "1px solid #e0e7ef", borderRadius: 3 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={6} md={2}>
              <TextField label="Model" name="model" fullWidth size="small" value={filters.model} onChange={handleChange} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField select label="Location" name="location" fullWidth size="small" value={filters.location} onChange={handleChange}>
                <MenuItem value="">All</MenuItem>
                {LOCATIONS.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField select label="Status" name="status" fullWidth size="small" value={filters.status} onChange={handleChange}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="IN_STOCK">IN STOCK</MenuItem>
                <MenuItem value="SOLD">SOLD</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField label="Reg. Number" name="registrationNumber" fullWidth size="small" value={filters.registrationNumber} onChange={handleChange} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField label="Owner Name" name="ownerName" fullWidth size="small" value={filters.ownerName} onChange={handleChange} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField label="Mobile" name="mobile" fullWidth size="small" value={filters.mobile} onChange={handleChange} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField select label="Month" name="month" fullWidth size="small" value={filters.month} onChange={handleChange}>
                <MenuItem value="">All</MenuItem>
                {[...Array(12)].map((_, i) => (
                  <MenuItem key={i + 1} value={i + 1}>
                    {new Date(0, i).toLocaleString("default", { month: "long" })}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField label="Year" name="year" type="number" fullWidth size="small" value={filters.year} onChange={handleChange} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField type="date" label="From Date" name="fromDate" fullWidth size="small" InputLabelProps={{ shrink: true }} value={filters.fromDate} onChange={handleChange} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField type="date" label="To Date" name="toDate" fullWidth size="small" InputLabelProps={{ shrink: true }} value={filters.toDate} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField label="Search" name="search" fullWidth size="small" value={filters.search} onChange={handleChange}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16 }} /></InputAdornment> }} />
            </Grid>
            <Grid item xs={6} md={3}>
              <Button fullWidth variant="contained" onClick={fetchData}
                sx={{ borderRadius: 2, fontWeight: 700, bgcolor: "#e65100", "&:hover": { bgcolor: "#bf360c" } }}>
                Apply Filter
              </Button>
            </Grid>
            <Grid item xs={6} md={3}>
              <Button fullWidth variant="outlined" onClick={resetFilters} sx={{ borderRadius: 2, fontWeight: 700 }}>
                Reset
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {/* ── SUMMARY CARDS ── */}
      {!loading && data.length > 0 && (
        <Grid container spacing={1.5} mb={2.5}>
          {[
            { label: "Total Exchange", value: totalBikes,                               color: "#e65100", bg: "#fff3e0" },
            { label: "In Stock",       value: totalStock,                               color: "#2e7d32", bg: "#e8f5e9" },
            { label: "Sold",           value: totalSold,                                color: "#1565c0", bg: "#e3f2fd" },
            { label: "Total Value",    value: `₹${totalValue.toLocaleString("en-IN")}`, color: "#6a1b9a", bg: "#f3e5f5" },
          ].map(({ label, value, color, bg }) => (
            <Grid item xs={6} md={3} key={label}>
              <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, bgcolor: bg, border: `1px solid ${color}33`, textAlign: "center" }}>
                <Typography variant="caption" color={color} fontWeight={700} letterSpacing={0.5} textTransform="uppercase" fontSize="0.65rem">
                  {label}
                </Typography>
                <Typography variant="h6" fontWeight={800} color={color}>{value}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── STATES ── */}
      {loading && <Box textAlign="center" py={6}><CircularProgress /></Box>}
      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!loading && data.length === 0 && (
        <Alert severity="info">No exchange bikes found for selected filters.</Alert>
      )}

      {/* ── SHOWROOM WISE SECTIONS ── */}
      {!loading && data.map((loc) => {
        const location = loc.location || "Other";
        const theme    = LOC_COLOR[location] || LOC_COLOR.Other;
        const isOpen   = expandedLoc[location] !== false;

        return (
          <Box key={location} mb={3}>
            <Paper
              elevation={0}
              onClick={() => toggleLoc(location)}
              sx={{
                px: 2.5, py: 1.5,
                background: `linear-gradient(135deg, ${theme.bg} 0%, #fff 100%)`,
                border: `2px solid ${theme.border}`,
                borderBottom: isOpen ? "none" : `2px solid ${theme.border}`,
                borderRadius: isOpen ? "12px 12px 0 0" : "12px",
                cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "space-between",
                flexWrap: "wrap", gap: 1
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <LocationOnIcon sx={{ color: theme.badge, fontSize: 22 }} />
                <Typography variant="subtitle1" fontWeight={800} color={theme.badge} letterSpacing={0.5}>
                  {location}
                </Typography>
                <Chip label={`${loc.totalExchange} bikes`} size="small"
                  sx={{ bgcolor: theme.badge, color: "#fff", fontWeight: 700, fontSize: "0.7rem" }} />
              </Box>
              <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                <Chip label={`${loc.inStock || 0} In Stock`} size="small"
                  sx={{ bgcolor: "#e8f5e9", color: "#2e7d32", fontWeight: 700, border: "1px solid #a5d6a7", fontSize: "0.68rem" }} />
                <Chip label={`${loc.sold || 0} Sold`} size="small"
                  sx={{ bgcolor: "#e3f2fd", color: "#1565c0", fontWeight: 700, border: "1px solid #90caf9", fontSize: "0.68rem" }} />
                <Typography variant="body2" fontWeight={700} color={theme.badge}>
                  ₹{(loc.totalValue || 0).toLocaleString("en-IN")}
                </Typography>
                {isOpen ? <ExpandLessIcon sx={{ color: theme.badge }} /> : <ExpandMoreIcon sx={{ color: theme.badge }} />}
              </Box>
            </Paper>

            <Collapse in={isOpen}>
              <Paper elevation={0} sx={{
                border: `2px solid ${theme.border}`, borderTop: "none",
                borderRadius: "0 0 12px 12px", overflow: "hidden"
              }}>
                <Box sx={{ overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: theme.light }}>
                        {["#", "Model", "Chasis", "Engine", "Reg No", "Customer", "Ex. Price", "Pay Status", "Stock Status", "Date", "Action"].map((h) => (
                          <TableCell key={h} sx={{ fontWeight: 800, fontSize: "0.7rem", color: theme.badge, letterSpacing: 0.5, whiteSpace: "nowrap", py: 1.2 }}>
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(!loc.bikes || loc.bikes.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={11} align="center" sx={{ py: 3, color: "#aaa", fontSize: "0.8rem" }}>
                            No bikes found for this location
                          </TableCell>
                        </TableRow>
                      )}
                      {loc.bikes?.map((b, idx) => (
                        <TableRow key={b._id} onClick={(e) => e.stopPropagation()}
                          sx={{ bgcolor: idx % 2 === 0 ? "#fff" : "#fafbff", "&:hover": { bgcolor: "#f0f4ff" }, transition: "background 0.15s" }}>

                          {/* # */}
                          <TableCell sx={{ color: "#aaa", fontSize: "0.73rem", fontWeight: 600 }}>{idx + 1}</TableCell>

                          {/* Model — from exchange stock */}
                          <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap", fontSize: "0.78rem" }}>
                            {b.model || "—"}
                            <Typography variant="caption" display="block" color="#999" fontSize="0.62rem">
                              {b.vehicleType === "E_RICKSHAW" ? "E-Rickshaw" : "Scooty"}
                            </Typography>
                          </TableCell>

                          {/* Chasis */}
                          <TableCell sx={{ fontSize: "0.75rem" }}>{b.chasis || "—"}</TableCell>

                          {/* Engine */}
                          <TableCell sx={{ fontSize: "0.75rem" }}>{b.engine || "—"}</TableCell>

                          {/* Reg No */}
                          <TableCell sx={{ fontSize: "0.75rem" }}>{b.registrationNumber || "—"}</TableCell>

                          {/* Customer from sale.customerName */}
                          <TableCell sx={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}>
                            {b.sale?.customerName || "—"}
                            {b.sale?.mobile?.[0] && (
                              <Typography variant="caption" display="block" color="#999" fontSize="0.62rem">
                                {b.sale.mobile[0]}
                              </Typography>
                            )}
                          </TableCell>

                          {/* Exchange Price from sale.exchange.exchangePrice */}
                          <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap", color: "#2e7d32" }}>
                            ₹{(b.sale?.exchange?.exchangePrice || b.purchasePrice || 0).toLocaleString("en-IN")}
                          </TableCell>

                          {/* Exchange Payment Status from sale.exchange.status */}
                          <TableCell>
                            {b.sale?.exchange ? (
                              <Chip
                                label={b.sale.exchange.status || "—"}
                                size="small"
                                sx={{
                                  fontSize: "0.65rem", fontWeight: 700,
                                  bgcolor: b.sale.exchange.status === "PAID" ? "#e8f5e9" : "#fff3e0",
                                  color:   b.sale.exchange.status === "PAID" ? "#2e7d32" : "#e65100",
                                  border:  `1px solid ${b.sale.exchange.status === "PAID" ? "#a5d6a7" : "#ffb74d"}`,
                                }}
                              />
                            ) : <Typography variant="caption" color="#bbb">—</Typography>}
                          </TableCell>

                          {/* Stock Status */}
                          <TableCell>
                            <Chip label={b.status || "—"} size="small" sx={{
                              fontSize: "0.65rem", fontWeight: 700,
                              bgcolor: b.status === "IN_STOCK" ? "#e8f5e9" : "#fce4ec",
                              color:   b.status === "IN_STOCK" ? "#2e7d32" : "#c62828",
                              border:  `1px solid ${b.status === "IN_STOCK" ? "#a5d6a7" : "#ef9a9a"}`
                            }} />
                          </TableCell>

                          {/* Date */}
                          <TableCell sx={{ fontSize: "0.73rem", color: "#666", whiteSpace: "nowrap" }}>
                            {b.date ? new Date(b.date).toLocaleDateString("en-IN") : "—"}
                          </TableCell>

                          {/* ── ACTION CELL ── */}
                          <TableCell>
                            <Box display="flex" gap={0.75} flexWrap="wrap">

                              {/* View — always visible */}
                              <Button size="small" variant="outlined"
                                onClick={(e) => { e.stopPropagation(); handleView(b._id); }}
                                sx={{ fontSize: "0.7rem", fontWeight: 700, borderRadius: 1.5, px: 1.2, py: 0.4, minWidth: 0 }}>
                                View
                              </Button>

                              {/* Sell — only IN_STOCK with ExchangeSellId */}
                              {b.status === "IN_STOCK" && b.ExchangeSellId && (
                                <Button size="small" variant="contained"
                                  onClick={(e) => { e.stopPropagation(); handleSellOpen(b); }}
                                  sx={{ fontSize: "0.7rem", fontWeight: 700, borderRadius: 1.5, px: 1.2, py: 0.4, minWidth: 0, bgcolor: "#e65100", "&:hover": { bgcolor: "#bf360c" } }}>
                                  Sell
                                </Button>
                              )}

                              {/* Update Payment — only SOLD with ExchangeSellId */}
                              {b.status === "SOLD" && b.ExchangeSellId && (
                                <Button size="small" variant="contained"
                                  onClick={(e) => { e.stopPropagation(); handleUpdateOpen(b); }}
                                  sx={{ fontSize: "0.7rem", fontWeight: 700, borderRadius: 1.5, px: 1.2, py: 0.4, minWidth: 0, bgcolor: "#1565c0", "&:hover": { bgcolor: "#0d47a1" } }}>
                                  Update
                                </Button>
                              )}

                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Paper>
            </Collapse>
          </Box>
        );
      })}

      {/* ══════════════════════════════════════════════════════════════════════
          🔍 DETAIL VIEW MODAL
      ══════════════════════════════════════════════════════════════════════ */}
      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} fullWidth maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{
          fontWeight: 800, bgcolor: "#e65100", color: "#fff",
          display: "flex", alignItems: "center", justifyContent: "space-between"
        }}>
          <Box display="flex" alignItems="center" gap={1}>
            <SwapHorizIcon /> Exchange Vehicle Details
          </Box>
          <IconButton onClick={() => setViewOpen(false)} sx={{ color: "#fff" }} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 2.5, bgcolor: "#f8fafc" }}>
          {detailLoading ? (
            <Box textAlign="center" py={5}><CircularProgress /></Box>
          ) : selected ? (
            <Grid container spacing={2}>

              {/* ── Stock / Vehicle Info ── */}
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #e0e7ef" }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                    <Typography fontWeight={800} color="#e65100" display="flex" alignItems="center" gap={0.5}>
                      <TwoWheelerIcon fontSize="small" /> Vehicle Info
                    </Typography>
                    <Box display="flex" gap={0.75}>
                      <Chip
                        label={selected?.vehicleType === "E_RICKSHAW" ? "E-Rickshaw" : "Scooty"}
                        size="small"
                        sx={{ fontWeight: 700, fontSize: "0.65rem",
                          bgcolor: selected?.vehicleType === "E_RICKSHAW" ? "#e3f2fd" : "#f3e5f5",
                          color:   selected?.vehicleType === "E_RICKSHAW" ? "#1565c0" : "#6a1b9a" }}
                      />
                      <Chip
                        label={selected?.status || "—"}
                        size="small"
                        sx={{ fontWeight: 700, fontSize: "0.65rem",
                          bgcolor: selected?.status === "IN_STOCK" ? "#e8f5e9" : "#fce4ec",
                          color:   selected?.status === "IN_STOCK" ? "#2e7d32" : "#c62828",
                          border:  `1px solid ${selected?.status === "IN_STOCK" ? "#a5d6a7" : "#ef9a9a"}` }}
                      />
                    </Box>
                  </Box>
                  <Grid container spacing={1.5}>
                    <Grid item xs={6}><InfoBlock label="Company"        value={selected?.companyName} /></Grid>
                    <Grid item xs={6}><InfoBlock label="Model"          value={selected?.model} /></Grid>
                    <Grid item xs={6}><InfoBlock label="Colour"         value={selected?.colour} /></Grid>
                    <Grid item xs={6}><InfoBlock label="Chasis No"      value={selected?.chasis} /></Grid>
                    <Grid item xs={6}><InfoBlock label="Engine No"      value={selected?.engine} /></Grid>
                    <Grid item xs={6}><InfoBlock label="Battery Type"   value={selected?.batteryType} /></Grid>
                    <Grid item xs={6}><InfoBlock label="Location"       value={selected?.currentLocation} /></Grid>
                    <Grid item xs={6}><InfoBlock label="Source"         value={selected?.source} /></Grid>
                    <Grid item xs={6}>
                      <InfoBlock
                        label="Purchase Price"
                        value={`₹${(selected?.purchasePrice || 0).toLocaleString("en-IN")}`}
                        color="#2e7d32"
                      />
                    </Grid>
                    {selected?.registrationNumber && (
                      <Grid item xs={6}><InfoBlock label="Reg. Number" value={selected.registrationNumber} /></Grid>
                    )}
                    <Grid item xs={6}>
                      <InfoBlock
                        label="Added On"
                        value={selected?.date ? new Date(selected.date).toLocaleDateString("en-IN") : "—"}
                      />
                    </Grid>
                    {selected?.saleDate && (
                      <Grid item xs={6}>
                        <InfoBlock
                          label="Sale Date"
                          value={new Date(selected.saleDate).toLocaleDateString("en-IN")}
                        />
                      </Grid>
                    )}
                    <Grid item xs={6}>
                      <InfoBlock label="Sl. No" value={selected?.slNo ? `#${selected.slNo}` : "—"} />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* ── Sale / Exchanger Info — from ExchangeSellId ── */}
              {soldInfo && (
                <Grid item xs={12}>
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #c8e6c9", bgcolor: "#f1f8e9" }}>

                    {/* Header row */}
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5} flexWrap="wrap" gap={0.75}>
                      <Typography fontWeight={800} color="#2e7d32" display="flex" alignItems="center" gap={0.5}>
                        <SellIcon fontSize="small" /> Sale Info
                      </Typography>
                      <Box display="flex" gap={0.75} flexWrap="wrap">
                        {soldInfo?.invoiceNo && (
                          <Chip
                            icon={<ReceiptLongIcon sx={{ fontSize: 13 }} />}
                            label={soldInfo.invoiceNo}
                            size="small"
                            sx={{ bgcolor: "#e8f5e9", color: "#1b5e20", fontWeight: 700, fontSize: "0.65rem", border: "1px solid #a5d6a7" }}
                          />
                        )}
                        {soldInfo?.saleType && (
                          <Chip label={soldInfo.saleType} size="small"
                            sx={{ bgcolor: "#e3f2fd", color: "#1565c0", fontWeight: 700, fontSize: "0.65rem" }} />
                        )}
                      </Box>
                    </Box>

                    {/* Customer details */}
                    <Grid container spacing={1.5}>
                      <Grid item xs={6}>
                        <InfoBlock label="Customer Name" value={soldInfo?.customerName} color="#1b5e20" />
                      </Grid>
                      <Grid item xs={6}>
                        <InfoBlock
                          label="Mobile"
                          value={Array.isArray(soldInfo?.mobile) ? soldInfo.mobile.join(", ") : soldInfo?.mobile || "—"}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <InfoBlock
                          label="Sale Date"
                          value={soldInfo?.saleDate ? new Date(soldInfo.saleDate).toLocaleDateString("en-IN") : "—"}
                        />
                      </Grid>
                      <Grid item xs={6}><InfoBlock label="Showroom"       value={soldInfo?.location} /></Grid>
                      <Grid item xs={6}><InfoBlock label="New Bike Model"  value={soldInfo?.model} /></Grid>
                      <Grid item xs={6}><InfoBlock label="Company"         value={soldInfo?.companyName} /></Grid>
                      <Grid item xs={6}><InfoBlock label="Engine No"       value={soldInfo?.engineNumber} /></Grid>
                      <Grid item xs={6}><InfoBlock label="Chasis No"       value={soldInfo?.chasisNumber} /></Grid>
                      <Grid item xs={6}>
                        <InfoBlock
                          label="Sale Price"
                          value={soldInfo?.salePrice ? `₹${soldInfo.salePrice.toLocaleString("en-IN")}` : "—"}
                          color="#1b5e20"
                        />
                      </Grid>
                      {soldInfo?.cashAmount > 0 && (
                        <Grid item xs={6}>
                          <InfoBlock
                            label="Cash Amount"
                            value={`₹${soldInfo.cashAmount.toLocaleString("en-IN")}`}
                            color="#1565c0"
                          />
                        </Grid>
                      )}
                    </Grid>

                    {/* ── Exchange Payment Block ── */}
                    {soldInfo?.exchange && (
                      <>
                        <Divider sx={{ my: 1.5 }} />
                        <Typography variant="caption" fontWeight={700} color="#555" letterSpacing={0.5} textTransform="uppercase" display="block" mb={1}>
                          Exchange Payment
                        </Typography>

                        {/* Exchange vehicle info */}
                        <Grid container spacing={1} mb={1}>
                          {soldInfo.exchange.ownerName && (
                            <Grid item xs={6}><InfoBlock label="Owner"      value={soldInfo.exchange.ownerName} /></Grid>
                          )}
                          {soldInfo.exchange.phone && (
                            <Grid item xs={6}><InfoBlock label="Phone"      value={soldInfo.exchange.phone} /></Grid>
                          )}
                          {soldInfo.exchange.model && (
                            <Grid item xs={6}><InfoBlock label="Model"      value={soldInfo.exchange.model} /></Grid>
                          )}
                          {soldInfo.exchange.registrationNumber && (
                            <Grid item xs={6}><InfoBlock label="Reg. No"    value={soldInfo.exchange.registrationNumber} /></Grid>
                          )}
                          {soldInfo.exchange.location && (
                            <Grid item xs={6}><InfoBlock label="Location"   value={soldInfo.exchange.location} /></Grid>
                          )}
                        </Grid>

                        {/* Payment summary banner */}
                        <Box sx={{ bgcolor: "#fff", borderRadius: 2, p: 1.5, border: "1px solid #c8e6c9", mb: 1 }}>
                          <Grid container spacing={1} textAlign="center">
                            <Grid item xs={4}>
                              <Typography variant="caption" color="#888" fontWeight={700} textTransform="uppercase" fontSize="0.6rem" display="block">
                                Agreed
                              </Typography>
                              <Typography variant="body2" fontWeight={800} color="#333">
                                ₹{(soldInfo.exchange.exchangePrice || 0).toLocaleString("en-IN")}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="caption" color="#888" fontWeight={700} textTransform="uppercase" fontSize="0.6rem" display="block">
                                Paid
                              </Typography>
                              <Typography variant="body2" fontWeight={800} color="#2e7d32">
                                ₹{(soldInfo.exchange.paidAmount || 0).toLocaleString("en-IN")}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="caption" color="#888" fontWeight={700} textTransform="uppercase" fontSize="0.6rem" display="block">
                                Remaining
                              </Typography>
                              <Typography variant="body2" fontWeight={800}
                                color={soldInfo.exchange.remainingAmount > 0 ? "#c62828" : "#2e7d32"}>
                                ₹{(soldInfo.exchange.remainingAmount || 0).toLocaleString("en-IN")}
                              </Typography>
                            </Grid>
                          </Grid>
                          <Box textAlign="center" mt={1}>
                            <Chip
                              label={soldInfo.exchange.status || "—"}
                              size="small"
                              sx={{
                                fontWeight: 800, fontSize: "0.65rem",
                                bgcolor: soldInfo.exchange.status === "PAID" ? "#e8f5e9" : "#fff3e0",
                                color:   soldInfo.exchange.status === "PAID" ? "#2e7d32" : "#e65100",
                                border:  `1px solid ${soldInfo.exchange.status === "PAID" ? "#a5d6a7" : "#ffb74d"}`,
                              }}
                            />
                          </Box>
                        </Box>

                        {/* Payment records */}
                        {payments.length > 0 ? (
                          <>
                            <Typography variant="caption" fontWeight={700} color="#888" letterSpacing={0.5} textTransform="uppercase" display="block" mb={0.75}>
                              Payment History
                            </Typography>
                            <Box display="flex" flexDirection="column" gap={0.75}>
                              {payments.map((p, i) => (
                                <Box key={i} display="flex" alignItems="center" justifyContent="space-between"
                                  sx={{ bgcolor: "#fff", borderRadius: 1.5, px: 1.5, py: 0.9, border: "1px solid #c8e6c9" }}>
                                  <Box>
                                    <Typography variant="body2" fontWeight={800} color="#2e7d32">
                                      ₹{(p.amount || 0).toLocaleString("en-IN")}
                                    </Typography>
                                    {p.name  && <Typography variant="caption" color="#555" fontWeight={600} display="block">{p.name}</Typography>}
                                    {p.phone && <Typography variant="caption" color="#888" display="block">📞 {p.phone}</Typography>}
                                  </Box>
                                  <Typography variant="caption" color="#999">
                                    {p.date ? new Date(p.date).toLocaleDateString("en-IN") : "—"}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </>
                        ) : (
                          <Typography variant="caption" color="#999">No payment records yet</Typography>
                        )}
                      </>
                    )}
                  </Paper>
                </Grid>
              )}

              {/* ── Remarks ── */}
              {selected?.remarks && (
                <Grid item xs={12}>
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #e0e7ef" }}>
                    <Typography fontWeight={800} color="#546e7a" mb={1}>📝 Remarks</Typography>
                    <Typography variant="body2">{selected.remarks}</Typography>
                  </Paper>
                </Grid>
              )}

            </Grid>
          ) : (
            <Alert severity="info">No details found</Alert>
          )}
        </DialogContent>
      </Dialog>

            {/* ── SELL MODAL ── */}
      <ExchangeSellModal
        open={sellOpen}
        onClose={() => setSellOpen(false)}
        bike={sellBike}
        onSuccess={() => { setSellOpen(false); fetchData(); }}
      />

      {/* ── UPDATE EXCHANGE PAYMENT MODAL ── */}
      <UpdateExchangePaymentModal
        open={openUpdate}
        onClose={() => setOpenUpdate(false)}
        bike={updateBike}
        onSuccess={() => { setOpenUpdate(false); fetchData(); }}
      />

    </Box>
  );
};

export default ExchangeBikeAdminView;